import keyBy from 'lodash/fp/keyBy';
import flow from 'lodash/flow';
import startOfDay from 'date-fns/startOfDay';
import { UserSetting, UserSettingTypes, BookCode, ProductCopyrightTerms } from 'types';
import { requestInFlight, requestSuccess, requestError, cancelLoading } from 'state/modules/entity';
import { setLocale } from 'translations/translator';
import { Dispatch } from 'redux';
import {
  getClickAways,
  getUserProfile,
  getUserTourSteps,
  updateUserSetting,
  updateClickAwayAccess,
  updateProductAccess,
  updateCopyrightTerms,
  getCopyrightTerms,
  getTourSteps,
  getBooks as getUserBooks,
  getAppBooks,
  getUserSettings as getUserSettingsApi,
  sendEmail as sendEmailApi,
  sendEmailLink as sendEmailLinkApi,
  sendWelcomeEmail as sendWelcomeEmailApi,
  saveTeachingOrderStartDateSetting
} from 'api';
import { changeAppLocale } from 'state/modules/app';
import { UserActionType } from '../types';
import { fetchMeSuccess, userActionError } from '../actions';
import isEmpty from 'lodash/isEmpty';
import { fetchToursSuccess } from 'state/modules/tour/actions';
import { updateSetting } from 'state/modules/user/actions';
import { formatFullDate } from 'util/date-util';
import { checkForAuditorUser } from '../util';

const stepNamesEqual = stepA => stepB => stepB.name === stepA.name;
const filterViewedSteps = toursViewed => tours => {
  if (isEmpty(toursViewed)) return tours;

  return tours
    .map(tour => ({
      ...tour,
      steps: tour.steps.filter(shouldViewStep(toursViewed, tour))
    }))
    .filter(tour => !isEmpty(tour.steps));
};
const shouldViewStep = (toursViewed, tour) => step => {
  const viewedTour = toursViewed[tour.name];
  if (isEmpty(viewedTour)) return true;

  const foundStep = viewedTour.steps.find(stepNamesEqual(step));
  return !foundStep || !foundStep.viewed;
};

const withSortIndex = books =>
  books.map((book, index) => {
    return { ...book, sortIndex: index };
  });

const getBooks = async user => {
  let books = [];

  try {
    ({ data: books } = await getUserBooks());

    return {
      books: withSortIndex(books),
      authorized: !!user.licenses.length || user.isAuditor,
      err: null
    };
  } catch (e) {
    const err = e as any;
    let error = err;

    const isForbidden =
      err.response?.status === 403 &&
      (err.response?.data?.code === 'NO_LICENSE' ||
        err.response?.data?.code === 'NO_ACTIVE_LICENSE');

    if (isForbidden) {
      try {
        const { data } = await getAppBooks();
        const clickAwayProducts =
          user.clickAwayAccess?.map(({ productCurriculumId }) => productCurriculumId) ?? [];

        books = data.filter(({ products }) =>
          products.find(({ productCurriculumId }) =>
            clickAwayProducts.includes(productCurriculumId)
          )
        );
      } catch (err) {
        error = err;
      }
    }

    return { books: withSortIndex(books), authorized: false, err: error };
  }
};

export function getUser() {
  return async (dispatch: Dispatch) => {
    dispatch({ type: UserActionType.USER_LOGIN_STARTED });

    let user;

    try {
      const params = {
        include: ['licenses', 'settings', 'clickAwayAccess', 'featureToggles']
      };

      ({ data: user } = await getUserProfile(params));

      if (user.settings?.locale) {
        setLocale(user.settings.locale);
        dispatch(changeAppLocale(user.settings.locale));
      }
      const { data: userTourSteps } = await getUserTourSteps({ viewed: false });

      if (user.settings?.copyrightTerms) {
        dispatch(requestSuccess({ copyrightTerms: user.settings.copyrightTerms }));
      }

      const isAuditor = checkForAuditorUser(user);

      const licenses = isAuditor
        ? []
        : user.licenses?.map(license => {
            if (license.dateExpiration) {
              license.expired = new Date(license.dateExpiration) < new Date();
            } else {
              license.expired = false;
            }
            return license;
          });

      const settings = {
        ...user.settings,
        locale: user.settings?.locale || 'en-US',
        tours: userTourSteps
      };

      dispatch(requestSuccess({ featureToggles: user.featureToggles }, { keyByProperty: 'name' }));

      const { data: tourSteps } = await getTourSteps();

      const tours = flow(filterViewedSteps(userTourSteps), keyBy('name'))(tourSteps);
      dispatch(fetchToursSuccess(tours));

      const { clickAwayAccess = [], ...restUser } = user;
      const clickAways = isAuditor ? [] : clickAwayAccess;

      const userData = { ...restUser, licenses, clickAways, settings, isAuditor };
      dispatch(fetchMeSuccess(userData));

      const { books, authorized, err } = await getBooks(userData);

      dispatch(requestSuccess({ workbooks: books }));

      if (!authorized) {
        dispatch(userActionError({ err, isForbidden: true }));
      }

      return { user, books, authorized };
    } catch (err) {
      return { authorized: false };
    }
  };
}

export function getUserSettings() {
  return async (dispatch: Dispatch) => {
    dispatch(requestInFlight());
    try {
      const settings = await getUserSettingsApi();

      return dispatch(requestSuccess({ settings }));
    } catch (err) {
      dispatch(requestError(err));
      dispatch(cancelLoading());
    }
  };
}

export function saveUserSetting(type: UserSettingTypes, newSetting: UserSetting) {
  return async (dispatch: Dispatch) => {
    const settingsForType = {
      [type]: newSetting
    };

    dispatch(requestInFlight());

    try {
      const settings = await updateUserSetting(type, settingsForType);

      return dispatch(requestSuccess({ settings }));
    } catch (err) {
      dispatch(requestError(err));
    }
  };
}

export function saveTeachingOrderStartDate(date: Date) {
  return async (dispatch: Dispatch) => {
    dispatch(requestInFlight());

    try {
      const setting = formatFullDate(startOfDay(date));
      const { data: teachingOrder } = await saveTeachingOrderStartDateSetting(setting);
      dispatch(updateSetting({ name: 'teachingOrder', value: teachingOrder }));
      dispatch(requestSuccess({}));
    } catch (err) {
      dispatch(requestError(err));
    }
  };
}

export function saveClickAwayAccess(bookCode: BookCode, edition: number) {
  return async (dispatch: Dispatch, getState) => {
    dispatch(requestInFlight());
    try {
      const { data } = await updateClickAwayAccess(bookCode, edition);
      const {
        user: { me, licenses = [], settings }
      } = getState();

      // transform object representation into array representation and remove products found in the licenses array
      const clickAways = (Object.entries(data) as any).reduce(
        (acc, [productCurriculumCode, caa]) => {
          if (
            licenses.find(
              ({ productCurriculumId }) => productCurriculumId === caa.productCurriculumId
            )
          )
            return acc;
          acc.push({ ...caa, productCurriculumCode });
          return acc;
        },
        []
      );

      await dispatch(
        fetchMeSuccess({
          ...me,
          licenses,
          clickAways,
          settings
        })
      );

      dispatch(requestSuccess({}));
      return clickAways;
    } catch (err) {
      dispatch(requestError(err));
    }
  };
}

export function saveProductAccess(productCurriculumId: number) {
  return async () => {
    try {
      await updateProductAccess(productCurriculumId);
    } catch (err) {
      // don't think this warrants a redirect to oops page, so swallow
    }
  };
}

export function saveCopyrightTerms(productCopyrightTerms: ProductCopyrightTerms) {
  return async (dispatch: Dispatch) => {
    dispatch(requestInFlight());

    try {
      await updateCopyrightTerms(productCopyrightTerms);
      const { data: copyrightTerms } = await getCopyrightTerms();

      return dispatch(requestSuccess({ copyrightTerms }));
    } catch (err) {
      dispatch(requestError(err));
    }
  };
}

export function sendEmail(message: string) {
  return async () => {
    await sendEmailApi(message);
  };
}

export async function sendEmailLink() {
  return sendEmailLinkApi();
}

export function sendWelcomeEmail() {
  return sendWelcomeEmailApi();
}

export const fetchClickAways = bookId => async (dispatch: Dispatch) => {
  dispatch(requestInFlight());
  try {
    const { data: clickAways } = await getClickAways(bookId, {
      include: ['tags']
    }); // CSC - need to re-add pages later...
    dispatch(requestSuccess({ clickAways }));
  } catch (error) {
    dispatch(requestError(error));
  }
};
